// Impresión masiva de documentos (docs/108 B8): facturas, boletas y notas de un período, elegidas en
// la grilla, se descargan en UN ZIP con un PDF por documento (original o cedible), listo para imprimir
// en lote. Cada PDF es el mismo del documento individual (con su timbre), verificado por el backend.
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, guardarArchivo, pedirArchivo } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { fecha, pesos } from '../comun/formato';
import { hoy } from '../compras/comun';
import { useSesion } from '../sesion/SesionContexto';

interface DocumentoListado {
  id: string;
  tipo_dte: number;
  folio: number | null;
  estado: string;
  estado_sii: string;
  monto_total: string | null;
  fecha_emision: string | null;
  receptor_rut: string | null;
  receptor_razon_social: string | null;
}

const TIPOS: Record<number, string> = {
  33: 'Factura',
  34: 'Factura exenta',
  39: 'Boleta',
  41: 'Boleta exenta',
  56: 'Nota de débito',
  61: 'Nota de crédito',
};

const primeroDelMes = () => `${hoy().slice(0, 8)}01`;

export function ImpresionMasiva() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState(hoy());
  const [tipo, setTipo] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [ejemplar, setEjemplar] = useState<'ambos' | 'original' | 'cedible'>('ambos');
  const [error, setError] = useState<unknown>(null);
  const [trabajando, setTrabajando] = useState(false);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'dte-impresion', desde, hasta, tipo],
    queryFn: () =>
      api.get<DocumentoListado[]>('/dte/documentos', {
        query: {
          empresaId,
          desde,
          hasta,
          tipoDte: tipo || undefined,
          estado: 'EMITIDO',
          limite: 500,
        },
      }),
  });
  const filas = q.data ?? [];
  const todos = filas.length > 0 && filas.every((d) => sel.has(d.id));
  const alternar = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const descargar = async (ids: string[]) => {
    setError(null);
    setTrabajando(true);
    try {
      const zip = await pedirArchivo('POST', '/dte/documentos/lote-pdf', {
        cuerpo: { empresaId, documentoIds: ids, ejemplar },
      });
      guardarArchivo(
        zip,
        `documentos-${desde}-${hasta}${ejemplar === 'cedible' ? '-cedible' : ''}.zip`,
      );
    } catch (e) {
      setError(e);
    } finally {
      setTrabajando(false);
    }
  };
  const abrir = async (id: string) => {
    setError(null);
    try {
      const pdf = await pedirArchivo('GET', `/dte/documentos/${id}/pdf`, { query: { ejemplar } });
      window.open(URL.createObjectURL(pdf), '_blank', 'noopener');
    } catch (e) {
      setError(e);
    }
  };
  return (
    <div className="pantalla consulta" data-pantalla="impresion-masiva">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Desde</span>
          <input
            type="date"
            aria-label="Desde"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Hasta</span>
          <input
            type="date"
            aria-label="Hasta"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Tipo</span>
          <select
            aria-label="Tipo de documento"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="">Todos</option>
            {Object.entries(TIPOS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Ejemplar</span>
          <select
            aria-label="Ejemplar"
            value={ejemplar}
            onChange={(e) => setEjemplar(e.target.value as 'ambos' | 'original' | 'cedible')}
          >
            <option value="ambos">Original y cedible</option>
            <option value="original">Solo original</option>
            <option value="cedible">Solo cedible (facturas y guías)</option>
          </select>
        </label>
        <span className="espaciador" />
        <span>{sel.size} elegidos</span>
        <button
          type="button"
          className="primario"
          disabled={!sel.size || trabajando}
          onClick={() => void descargar([...sel])}
        >
          {trabajando ? 'Preparando…' : 'Descargar para imprimir (ZIP)'}
        </button>
      </div>
      {q.isError || error ? <PanelError error={q.error ?? error} /> : null}
      <table className="grilla compacta" aria-label="Documentos emitidos">
        <thead>
          <tr>
            <th style={{ width: '2rem' }}>
              <input
                type="checkbox"
                aria-label="Elegir todos"
                checked={todos}
                onChange={() => setSel(todos ? new Set() : new Set(filas.map((d) => d.id)))}
              />
            </th>
            <th>Tipo</th>
            <th>Folio</th>
            <th>Fecha</th>
            <th>Receptor</th>
            <th className="der">Total</th>
            <th>SII</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filas.map((d) => (
            <tr key={d.id}>
              <td>
                <input
                  type="checkbox"
                  aria-label={`Elegir ${TIPOS[d.tipo_dte] ?? d.tipo_dte} ${d.folio}`}
                  checked={sel.has(d.id)}
                  onChange={() => alternar(d.id)}
                />
              </td>
              <td>{TIPOS[d.tipo_dte] ?? d.tipo_dte}</td>
              <td className="mono">{d.folio ?? '—'}</td>
              <td>{fecha(d.fecha_emision)}</td>
              <td>{d.receptor_razon_social ?? d.receptor_rut ?? ''}</td>
              <td className="der">{pesos(d.monto_total)}</td>
              <td>{d.estado_sii.toLowerCase()}</td>
              <td>
                <button type="button" className="boton-mini" onClick={() => void abrir(d.id)}>
                  Ver PDF
                </button>
              </td>
            </tr>
          ))}
          {!filas.length && (
            <tr>
              <td colSpan={8} className="grilla-vacia">
                {q.isLoading ? 'Cargando…' : 'Sin documentos emitidos en el período'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
